import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Search,
  Filter,
  PlusCircle,
  Building2,
  ChevronRight,
  Trash2,
  MoreVertical,
  ClipboardList
} from 'lucide-react'
import { Button, Card, Input, Select, ConfirmModal } from '../components/common'
import useSurveyStore from '../stores/surveyStore'
import { formatDate } from '../utils/helpers'
import { SURVEY_STATUS, SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../utils/constants'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileSurveyList from '../components/mobile/MobileSurveyList'

export default function SurveyListPage() {
  const isMobile = useMobileLayout()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const surveys = useSurveyStore(state => state.surveys)
  const loading = useSurveyStore(state => state.loading)
  const loadSurveys = useSurveyStore(state => state.loadSurveys)
  const deleteSurvey = useSurveyStore(state => state.deleteSurvey)

  useEffect(() => {
    loadSurveys()
  }, [loadSurveys])

  // Mobile layout
  if (isMobile) {
    return <MobileSurveyList />
  }

  // Filter surveys
  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch =
      survey.propertyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      survey.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      survey.postcode.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = !statusFilter || survey.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const handleDelete = async () => {
    if (!deleteConfirm) return
    await deleteSurvey(deleteConfirm.id)
    setDeleteConfirm(null)
  }

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: SURVEY_STATUS.IN_PROGRESS, label: 'In Progress' },
    { value: SURVEY_STATUS.COMPLETED, label: 'Completed' },
    { value: SURVEY_STATUS.ANALYSED, label: 'Analysed' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">All Surveys</h1>
          <p className="text-slate-500 mt-1">
            {surveys.length} total surveys
          </p>
        </div>
        <Button variant="primary" icon="PlusCircle" onClick={() => navigate('/survey/new')}>
          New Survey
        </Button>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, address, or postcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon="Search"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
              placeholder="Filter by status"
            />
          </div>
        </div>
      </Card>

      {/* Survey list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filteredSurveys.length > 0 ? (
        <div className="space-y-3">
          {filteredSurveys.map((survey) => (
            <Card
              key={survey.id}
              hover
              className="flex items-center gap-4"
            >
              {/* Icon */}
              <div
                className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0 cursor-pointer"
                onClick={() => navigate(`/survey/${survey.id}`)}
              >
                <Building2 className="w-7 h-7 text-primary-600" />
              </div>

              {/* Content */}
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => navigate(`/survey/${survey.id}`)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {survey.propertyName}
                  </h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SURVEY_STATUS_COLORS[survey.status]}`}>
                    {SURVEY_STATUS_LABELS[survey.status]}
                  </span>
                </div>
                <p className="text-sm text-slate-500 truncate">
                  {survey.address}, {survey.postcode}
                </p>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                  <span>Surveyor: {survey.surveyorName}</span>
                  <span>Created: {formatDate(survey.createdAt)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="Trash2"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeleteConfirm(survey)
                  }}
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="ChevronRight"
                  onClick={() => navigate(`/survey/${survey.id}`)}
                  className="text-slate-400"
                />
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            {searchQuery || statusFilter ? 'No surveys found' : 'No surveys yet'}
          </h3>
          <p className="text-slate-500 mb-4">
            {searchQuery || statusFilter
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first property survey to get started'
            }
          </p>
          {!searchQuery && !statusFilter && (
            <Button variant="primary" icon="PlusCircle" onClick={() => navigate('/survey/new')}>
              Create Survey
            </Button>
          )}
        </Card>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Survey"
        message={`Are you sure you want to delete "${deleteConfirm?.propertyName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  )
}
